import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight, ArrowLeft, Trophy, Loader2, Send } from 'lucide-react';
import './Assignments.css'; // تأكد من إنشاء هذا الملف

const AssignmentsPage = () => {
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [score, setScore] = useState(null);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchQuestions = async () => {
            setLoading(true);
            try {
                const lesson_id = localStorage.getItem('lesson_id');
                const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/questions/lesson/${lesson_id}`);
                setQuestions(response.data);
            } catch (error) {
                console.error('Error fetching questions:', error);
                setError('لا يوجد واجبات متاحة لهذه المحاضرة حالياً.');
            } finally {
                setLoading(false);
            }
        };
        fetchQuestions();
    }, []);

    const handleOptionChange = (questionId, selectedOption) => {
        if (submitted) return; // منع التعديل بعد التسليم
        setAnswers({
            ...answers,
            [questionId]: selectedOption,
        });
    };

    const handleSubmit = async () => {
        // التحقق من الإجابة على جميع الأسئلة (اختياري، يمكنك إزالته إذا أردت السماح بالتسليم الناقص)
        if (Object.keys(answers).length < questions.length) {
            const confirmSubmit = window.confirm("لم تقم بالإجابة على جميع الأسئلة، هل أنت متأكد من رغبتك في التسليم؟");
            if (!confirmSubmit) return;
        }

        let correctAnswers = 0;
        questions.forEach((question) => {
            if (answers[question.id] && answers[question.id] === question.correct_answer) {
                correctAnswers += 1;
            }
        });

        const calculatedScore = correctAnswers;
        setScore(calculatedScore);
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' }); // الصعود للأعلى لرؤية النتيجة

        try {
            const userId = localStorage.getItem('id');
            const lessonId = localStorage.getItem('lesson_id');

            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/submit-score`, {
                user_id: userId,
                lesson_id: lessonId,
                score: calculatedScore,
            });
            console.log('Score submitted successfully:', response.data);
            // alert(response.data.message); 
        } catch (error) {
            console.error('Error submitting score:', error);
        }
    };

    const handleBack = () => {
        navigate('/Video');
    };

    if (error) {
        return (
            <div className="quiz-error-container">
                
                <div className="quiz-error-card">
                    <XCircle size={50} className="error-icon" />
                    <h2>عفواً!</h2>
                    <p>{error}</p>
                    <button className="btn-back-outline" onClick={handleBack}>
                        <ArrowRight size={18} /> العودة للمحاضرة
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="assignments-wrapper">
            <div className="assignments-container">

                {/* رأس الصفحة */}
                <header className="quiz-header">
                    
                    <motion.button 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="back-nav-btn" 
            onClick={() => navigate('/Video')}
          >
            <ArrowRight size={20} />
            <span>الرجوع للفيديو</span>
          </motion.button>
                    <h1>تطبيق تفاعلي <span>(الواجب)</span></h1>
                    <div className="spacer"></div>
                </header>

                {loading ? (
                    <div className="quiz-loader">
                        <Loader2 className="spinner" size={40} />
                        <p>جاري تحضير الأسئلة...</p>
                    </div>
                ) : (
                    <>
                        {/* النتيجة تظهر هنا بعد التسليم */}
                        <AnimatePresence>
                            {submitted && (
                                <motion.div
                                    className="score-banner"
                                    initial={{ opacity: 0, y: -20, scale: 0.9 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                >
                                    <Trophy size={50} className="trophy-icon" />
                                    <div className="score-details">
                                        <h2>اكتمل الاختبار!</h2>
                                        <p>لقد حصلت على</p>
                                        <div className="score-numbers">
                                            <span className="points">{score}</span>
                                            <span className="out-of">من {questions.length}</span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* قائمة الأسئلة */}
                        <div className="questions-list">
                            {Array.isArray(questions) && questions.map((question, index) => (
                                <motion.div
                                    key={question.id}
                                    className="question-modern-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <div className="question-header">
                                        <span className="q-badge">سؤال {index + 1}</span>
                                        <h3 className="q-text">{question.question_text}</h3>
                                    </div>

                                    {question.answer_type === 'multiple_choice' && question.options && (
                                        <div className="options-grid">
                                            {JSON.parse(question.options).map((option, idx) => {
                                                const isSelected = answers[question.id] === option;
                                                const isCorrect = option === question.correct_answer;

                                                // تحديد حالة الكلاسات للتصميم
                                                let optionClass = "option-card";
                                                if (isSelected) optionClass += " selected";
                                                if (submitted) {
                                                    if (isCorrect) optionClass += " correct";
                                                    else if (isSelected && !isCorrect) optionClass += " wrong";
                                                }

                                                return (
                                                    <div
                                                        key={idx}
                                                        className={optionClass}
                                                        onClick={() => handleOptionChange(question.id, option)}
                                                    >
                                                        <div className="radio-circle">
                                                            {isSelected && <div className="radio-dot"></div>}
                                                        </div>
                                                        <span className="option-text">{option}</span>

                                                        {submitted && isCorrect && <CheckCircle2 className="status-icon success" size={20} />}
                                                        {submitted && isSelected && !isCorrect && <XCircle className="status-icon danger" size={20} />}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {question.answer_type === 'open_ended' && (
                                        <div className="open-ended-container">
                                            <textarea
                                                className={`open-input ${submitted && (answers[question.id] === question.correct_answer ? 'correct-border' : 'wrong-border')}`}
                                                placeholder="اكتب إجابتك هنا..."
                                                value={answers[question.id] || ''}
                                                onChange={(e) => handleOptionChange(question.id, e.target.value)}
                                                disabled={submitted}
                                                rows="3"
                                            />
                                            {submitted && (
                                                <div className={`feedback-alert ${answers[question.id] === question.correct_answer ? 'success-alert' : 'danger-alert'}`}>
                                                    {answers[question.id] === question.correct_answer ? (
                                                        <><CheckCircle2 size={18} /> إجابة صحيحة!</>
                                                    ) : (
                                                        <><XCircle size={18} /> الإجابة الصحيحة هي: <strong>{question.correct_answer}</strong></>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        {/* أزرار التحكم السفلية */}
                        <div className="quiz-footer-actions">
                            {!submitted ? (
                                <button className="btn-submit-quiz" onClick={handleSubmit}>
                                    <span>تسليم الإجابات</span>
                                    <Send size={18} />
                                </button>
                            ) : (
                                <button className="btn-back-home" onClick={handleBack}>
                                    <ArrowRight size={18} />
                                    <span>العودة للدرس</span>
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AssignmentsPage;